/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ScrollingModule } from '@angular/cdk/scrolling';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CurrencyLineComponent } from '@app-settings/estimate-general/currency-line/currency-line.component';
import { EstimateGeneralSettingsComponent } from '@app-settings/estimate-general/estimate-general-settings.component';
import { ServiceTypePickerComponent } from '@app-settings/estimate-general/service-type-picker/service-type-picker.component';
import { EstimateInterfaceSettingsComponent } from '@app-settings/estimate-interface/estimate-interface-settings.component';
import { LicenseUserTableComponent } from '@app-settings/services-licenses/license-user-table/license-user-table.component';
import { ServicesLicensesSettingsComponent } from '@app-settings/services-licenses/services-licenses-settings.component';
import { SettingsOverrideComponent } from '@app-settings/settings-override/settings-override.component';
import { SettingsComponent } from '@app-settings/settings.component';
import { AppComponent } from '@core/app.component';
import { AuthInterceptor } from '@core/authentification/auth.interceptor';
import { CoreModule } from '@core/core.module';
import { LicenseExpiredComponent } from '@core/error-page/license-expired/license-expired.component';
import { UnauthorizedAccessComponent } from '@core/error-page/unauthorized-access/unauthorized-access.component';
import { UserLockedComponent } from '@core/error-page/user-locked/user-locked.component';
import { PopErrorHandler } from '@core/global/pop-error-handler';
import { ErrorInterceptor } from '@core/helpers/error.interceptor';
import { LandingPageComponent } from '@core/landing-page/landing-page.component';
import { DataManagmentComponent } from '@data-managment/data-managment.component';
import { DefaultDepartmentSelectorComponent } from '@data-managment/default-department-selector/default-department-selector.component';
import { DepartmentPickerComponent } from '@data-managment/department-picker/department-picker.component';
import { EstimateExportComponent } from '@data-managment/estimate-export/estimate-export.component';
import { EstimatePickerComponent } from '@data-managment/estimate-picker/estimate-picker.component';
import { ExportToGpSifiaComponent } from '@data-managment/export-to-gp-sifia/export-to-gp-sifia.component';
import { ProjectImportComponent } from '@data-managment/project-import/project-import.component';
import { ProjectPickerComponent } from '@data-managment/project-picker/project-picker.component';
import { ResourcePickerComponent } from '@data-managment/resource-picker/resource-picker.component';
import { CreateEstimateComponent } from '@estimate/create-estimate/create-estimate.component';
import { DeployDialogComponent } from '@estimate/deploy-dialog/deploy-dialog.component';
import { ErrorDialogComponent } from '@estimate/error-dialog/error-dialog.component';
import { EstimateBudgetTypeComponent } from '@estimate/estimate-budget-types/estimate-budget-type/estimate-budget-type.component';
import { EstimateBudgetTypesComponent } from '@estimate/estimate-budget-types/estimate-budget-types.component';
import { EstimateComponent } from '@estimate/estimate.component';
import { EstimateModule } from '@estimate/estimate.module';
import { ExpansionPanelComponent } from '@estimate/expansion-panel/expansion-panel.component';
import { StatusChipComponent } from '@estimate/expansion-panel/status-chip/status-chip.component';
import { GridDisplayFilterComponent } from '@estimate/grid-display-filter/grid-display-filter.component';
import { EstimateListComponent } from '@estimate/list/estimate-list.component';
import { CategoryTableComponent } from '@estimate/table-views/category-table/category-table.component';
import { ClassTableComponent } from '@estimate/table-views/class-table/class-table.component';
import { DepartmentTableComponent } from '@estimate/table-views/department-table/department-table.component';
import { ClassModalComponent } from '@estimate/toolbar/class-modal/class-modal.component';
import { DepartmentModalComponent } from '@estimate/toolbar/department-modal/department-modal.component';
import { DetailedAssociationDialogComponent } from '@estimate/toolbar/department-modal/detailed-association-dialog/detailed-association-dialog.component';
import { InternalResourceModalComponent } from '@estimate/toolbar/internal-resources-modal/internal-resources-modal.component';
import { PropertiesModalComponent } from '@estimate/toolbar/properties-modal/properties-modal.component';
import { ImportModalComponent } from '@estimate/toolbar/structure-modal/import-modal/import-modal.component';
import { StructureModalComponent } from '@estimate/toolbar/structure-modal/structure-modal.component';
import { TabsComponent } from '@estimate/toolbar/tabs/tabs.component';
import { ToolbarComponent } from '@estimate/toolbar/toolbar.component';
import { ColumnToggleComponent } from '@estimate/treetable/column-toggle/column-toggle.component';
import { EstimateTreeCellComponent } from '@estimate/treetable/estimate-tree-cell/estimate-tree-cell.component';
import { ExpenseCellComponent } from '@estimate/treetable/expense-cell/expense-cell.component';
import { TreetableComponent } from '@estimate/treetable/treetable.component';
import { UserCardComponent } from '@estimate/user-card/user-card.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PipesModule } from '@pipes/pipes.module';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { ChevronComponent } from '@shared/components/chevron/chevron.component';
import { LoadingComponent } from '@shared/components/loading.component';
import { SettingsBoxComponent } from '@shared/components/settings-box/settings-box.component';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { TreeCellDropdownComponent } from '@shared/components/tree-cell-dropdown/tree-cell-dropdown.component';
import { ClickOutsideElementDirective } from '@shared/directives/click-outside-element.directive';
import { FeatureDirective } from '@shared/directives/feature.directive';
import { InputDirective } from '@shared/directives/input.directive';
import { AuthGuard } from '@shared/services/auth.guard';
import { AuthService } from '@shared/services/auth.service';
import { BaseService } from '@shared/services/base.service';
import { TotalsDataService } from '@shared/services/total.service';
import { SharedModule } from '@shared/shared.module';
import { AjaxHttpClientAdapter } from 'breeze-client/adapter-ajax-httpclient';
import { DataServiceWebApiAdapter } from 'breeze-client/adapter-data-service-webapi';
import { ModelLibraryBackingStoreAdapter } from 'breeze-client/adapter-model-library-backing-store';
import { UriBuilderODataAdapter } from 'breeze-client/adapter-uri-builder-odata';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TreeTableModule } from 'primeng/treetable';
import { AppRoutingModule } from './app-routing.module';

// the second parameter 'fr' is optional
registerLocaleData(localeFr, 'fr');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    ClassModalComponent,
    InternalResourceModalComponent,
    DepartmentModalComponent,
    EstimateComponent,
    ExpansionPanelComponent,
    EstimateListComponent,
    StructureModalComponent,
    ToolbarComponent,
    TreetableComponent,
    CategoryTableComponent,
    ClassTableComponent,
    DepartmentTableComponent,
    ImportModalComponent,
    UserCardComponent,
    SettingsComponent,
    UserLockedComponent,
    DeployDialogComponent,
    EstimateGeneralSettingsComponent,
    EstimateInterfaceSettingsComponent,
    ServicesLicensesSettingsComponent,
    LicenseExpiredComponent,
    LandingPageComponent,
    LicenseExpiredComponent,
    UnauthorizedAccessComponent,
    LoadingComponent,
    LicenseUserTableComponent,
    ColumnToggleComponent,
    CurrencyLineComponent,
    ExpenseCellComponent,
    EstimateTreeCellComponent,
    DataManagmentComponent,
    DepartmentPickerComponent,
    DefaultDepartmentSelectorComponent,
    ProjectImportComponent,
    EstimateExportComponent,
    SwitchComponent,
    CheckboxComponent,
    ChevronComponent,
    SettingsBoxComponent,
    ProjectPickerComponent,
    TabsComponent,
    EstimatePickerComponent,
    SettingsOverrideComponent,
    FeatureDirective,
    InputDirective,
    ErrorDialogComponent,
    DetailedAssociationDialogComponent,
    GridDisplayFilterComponent,
    TreeCellDropdownComponent,
    ServiceTypePickerComponent,
    ExportToGpSifiaComponent,
    CreateEstimateComponent,
    EstimateBudgetTypesComponent,
    EstimateBudgetTypeComponent,
    PropertiesModalComponent,
    DepartmentModalComponent,
    ResourcePickerComponent,
    ClickOutsideElementDirective,
    StatusChipComponent
  ],
  imports: [
    AppRoutingModule,
    CoreModule,
    ConfirmDialogModule,
    BrowserAnimationsModule,
    BrowserModule,
    TreeTableModule,
    FontAwesomeModule,
    FormsModule,
    HttpClientModule,
    FileUploadModule,
    ReactiveFormsModule,
    PipesModule,
    SharedModule,
    OverlayPanelModule,
    ProgressSpinnerModule,
    EstimateModule,
    TableModule,
    ScrollingModule,
    MessagesModule,
    MessageModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      isolate: false
    }),
    LoggerModule.forRoot({
      serverLoggingUrl: BaseService.getBaseUrl() + '/EAP/logs',
      level: NgxLoggerLevel.TRACE,
      serverLogLevel: NgxLoggerLevel.WARN,
      disableConsoleLogging: false
    }),
    ToastModule
  ],

  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    AuthGuard,

    TotalsDataService,

    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },

    {
      provide: ErrorHandler,
      useClass: PopErrorHandler
    },
    MessageService,
    ConfirmationService,
    AuthService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  public constructor(http: HttpClient) {
    /**
     * Breeze Adapter Registrations.
     * Needed since updating to Breeze version 2.0.
     *
     * Explanation: the aggressive tree-shaking of tsickle/terser/webpack in Angular 8
     * removes the functions that the Breeze adapters use to register themselves!
     * So you need to register them yourself.
     * Source: https://github.com/Breeze/breeze-client/blob/master/UPGRADE.md
     **/
    ModelLibraryBackingStoreAdapter.register();
    UriBuilderODataAdapter.register(); //Use UriBuilderJsonAdapter if backend is .NetCore/.Net5
    AjaxHttpClientAdapter.register(http);
    DataServiceWebApiAdapter.register();
  }
}
